import type { RouteLocationNormalized } from 'vue-router';
import { i18n } from '@/config/i18n.ts';
import {
  PolicyStatus,
  type OrganizationRisk,
  type OrganizationRiskCategory,
  type OrganizationRiskThreat,
} from '@/clients/v2';
import { SHARED_TABS } from '@/utils/tabs.ts';

/**
 * Generic interface for entities that can be displayed in breadcrumbs
 */
interface ProjectBreadcrumbEntity {
  id: string;
  name?: string | null;
}

interface OrganizationBreadcrumbEntity {
  code?: string;
  id?: string;
  name: string;
}

/**
 * Creates a breadcrumb function for stores with different property names
 * Uses function overloads for better type safety
 */
function createAdaptedBreadcrumb(
  entityType: string,
  store: {
    [key: string]: ProjectBreadcrumbEntity | OrganizationBreadcrumbEntity | null | undefined;
  },
  entityProperty: string,
  paramName: string,
) {
  return (route: RouteLocationNormalized): string => {
    const entityId = route.params[paramName] as string;
    const entity = store[entityProperty] as
      ProjectBreadcrumbEntity | OrganizationBreadcrumbEntity | null;

    // If entity data is available and matches the route parameter, use the name
    if (entity && entity.id === entityId) {
      return entity.name || entity.id || '';
    }

    // Fallback to translated entity type with ID
    return `${i18n.global.t(entityType)} ${entityId}`;
  };
}

/**
 * Creates a simple static breadcrumb using translation.
 * Exposed as `.t` so vue-i18n-extract detects keys (same pattern as i18n `.t` calls).
 */
const createStaticBreadcrumb = {
  t(translationKey: string): string {
    return i18n.global.t(translationKey);
  },
};

/**
 * Breadcrumb factory for common entity types
 * Returns functions that will be called when the route is accessed
 */
export const BreadcrumbFactory = {
  /**
   * Creates a project breadcrumb
   */
  project:
    (getStore: () => { project?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('AI System', store, 'project', 'id')(route);
    },

  /**
   * Creates a requirement breadcrumb
   */
  requirement:
    (getStore: () => { currentRequirement?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb(
        'Requirement',
        store,
        'currentRequirement',
        'requirementId',
      )(route);
    },

  /**
   * Creates a risk breadcrumb
   */
  risk:
    (getStore: () => { currentRisk?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Risk', store, 'currentRisk', 'riskId')(route);
    },

  /**
   * Creates a project threat breadcrumb
   */
  projectThreat:
    (getStore: () => { currentThreat?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Threat', store, 'currentThreat', 'threatId')(route);
    },

  /**
   * Creates an asset breadcrumb
   */
  asset:
    (getStore: () => { currentAsset?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Asset', store, 'currentAsset', 'assetId')(route);
    },

  /**
   * Creates a test breadcrumb
   */
  test:
    (getStore: () => { currentTestingTest?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Test', store, 'currentTestingTest', 'testId')(route);
    },

  /**
   * Creates an agent test breadcrumb
   */
  agentTest:
    (getStore: () => { currentAgentTest?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Test', store, 'currentAgentTest', 'agentTestId')(route);
    },

  /**
   * Creates a job breadcrumb
   */
  job:
    (getStore: () => { currentJob?: ProjectBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Job', store, 'currentJob', 'jobId')(route);
    },

  /**
   * Creates an AI usage organization breadcrumb
   */
  aiUsageOrganization:
    (
      getStore: () => {
        organizationOverview?: { organization_id: string; organization_name: string } | null;
      },
    ) =>
    (route: RouteLocationNormalized) => {
      const overview = getStore().organizationOverview;
      const organizationId = route.params.organizationId as string;

      if (overview && overview.organization_id === organizationId) {
        return overview.organization_name || organizationId;
      }

      return i18n.global.t('Organization usage');
    },

  /**
   * Creates a framework breadcrumb
   */
  framework:
    (getStore: () => { frameworkData?: OrganizationBreadcrumbEntity | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      return createAdaptedBreadcrumb('Framework', store, 'frameworkData', 'code')(route);
    },

  /**
   * Creates an organization taxonomy category breadcrumb
   */
  organizationTaxonomyCategory:
    (getStore: () => { currentCategory?: OrganizationRiskCategory | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();

      return createAdaptedBreadcrumb(
        'OrganizationTaxonomyCategory',
        store,
        'currentCategory',
        'categoryId',
      )(route);
    },

  /**
   * Creates an organization taxonomy risk breadcrumb
   */
  organizationTaxonomyRisk:
    (getStore: () => { currentRisk?: OrganizationRisk | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();

      return createAdaptedBreadcrumb(
        'OrganizationTaxonomyRisk',
        store,
        'currentRisk',
        'riskId',
      )(route);
    },

  /**
   * Creates an organization taxonomy threat breadcrumb
   */
  organizationTaxonomyThreat:
    (getStore: () => { currentThreat?: OrganizationRiskThreat | null }) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();

      return createAdaptedBreadcrumb(
        'OrganizationTaxonomyThreat',
        store,
        'currentThreat',
        'threatId',
      )(route);
    },

  /**
   * Creates a policy breadcrumb
   */
  policy:
    (
      getStore: () => {
        currentPolicy?:
          | (ProjectBreadcrumbEntity & {
              status?: PolicyStatus;
              draft_policy_version_id?: string | null;
            })
          | null;
      },
    ) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      const label = createAdaptedBreadcrumb('Policy', store, 'currentPolicy', 'policyId')(route);
      const policy = store.currentPolicy;

      if (policy?.status === PolicyStatus.Unpublished && policy.draft_policy_version_id) {
        const policyId = route.params.policyId as string;
        const isManagement = route.matched.some((r) => r.name === 'management');
        const basePath = isManagement ? '/policies/management' : '/policies/my-policies';
        return {
          label,
          to: `${basePath}/${policyId}/versions/${policy.draft_policy_version_id}`,
        };
      }

      return label;
    },

  /**
   * Creates a policy versions breadcrumb (hidden for draft versions)
   */
  policyVersions:
    (
      getStore: () => {
        currentPolicy?: { draft_policy_version_id?: string | null } | null;
      },
    ) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      const versionId = route.params.versionId as string;
      const policyId = route.params.policyId as string;

      if (store.currentPolicy?.draft_policy_version_id === versionId) {
        return '';
      }

      const isManagement = route.matched.some((r) => r.name === 'management');
      const basePath = isManagement ? '/policies/management' : '/policies/my-policies';
      return {
        label: i18n.global.t('Versions'),
        to: `${basePath}/${policyId}?tab=${SHARED_TABS.POLICY_VERSION_HISTORY}`,
      };
    },

  /**
   * Creates a policy version breadcrumb
   */
  policyVersion:
    (
      getStore: () => {
        currentPolicyVersion: (ProjectBreadcrumbEntity & { version_number?: number | null }) | null;
        currentPolicy?:
          | (ProjectBreadcrumbEntity & {
              draft_policy_version_id?: string | null;
              status?: PolicyStatus;
              policy_version_id?: string | null;
            })
          | null;
      },
    ) =>
    (route: RouteLocationNormalized) => {
      const store = getStore();
      const versionId = route.params.versionId as string;
      const version = store.currentPolicyVersion;

      const isDraftVersion =
        store.currentPolicy?.draft_policy_version_id === store.currentPolicyVersion?.id;

      if (isDraftVersion) {
        // Unpublished policies: no "Draft" crumb — breadcrumb stops at policy name
        if (
          store.currentPolicy?.status === PolicyStatus.Unpublished ||
          (store.currentPolicy?.status === PolicyStatus.Archived &&
            !store.currentPolicy.policy_version_id)
        ) {
          return '';
        }

        return i18n.global.t('Draft');
      }

      if (version && version.id === versionId) {
        if (version.version_number != null) {
          return `v${version.version_number}`;
        }
        return version.name || version.id;
      }

      return `${i18n.global.t('Policy Version')} ${versionId}`;
    },

  /**
   * Creates static breadcrumbs for common sections
   */
  static: {
    aiRegistry: () => createStaticBreadcrumb.t('AI Registry'),
    overview: () => createStaticBreadcrumb.t('Overview'),
    projects: () => createStaticBreadcrumb.t('AI Systems'),
    mySubmissions: () => createStaticBreadcrumb.t('My Submissions'),
    requirements: () => createStaticBreadcrumb.t('Requirements'),
    controls: () => createStaticBreadcrumb.t('Controls'),
    graph: () => createStaticBreadcrumb.t('Graph'),
    settings: () => createStaticBreadcrumb.t('Settings'),
    risks: () => createStaticBreadcrumb.t('Risks'),
    riskQuantification: () => createStaticBreadcrumb.t('Quantification'),
    evidence: () => createStaticBreadcrumb.t('Evidence'),
    assets: () => createStaticBreadcrumb.t('Assets'),
    testing: () => createStaticBreadcrumb.t('Runtime Inspection'),
    jobs: () => createStaticBreadcrumb.t('Jobs'),
    organization: () => createStaticBreadcrumb.t('Organization'),
    organizationRiskManagement: () => createStaticBreadcrumb.t('Risk Management'),
    organizationUsers: () => createStaticBreadcrumb.t('Users'),
    organizationFrameworks: () => createStaticBreadcrumb.t('Frameworks'),
    organizationCustomization: () => createStaticBreadcrumb.t('Customization'),
    organizationSettings: () => createStaticBreadcrumb.t('Settings'),
    organizationNotifications: () => createStaticBreadcrumb.t('Notifications Center'),
    organizationUsage: () => createStaticBreadcrumb.t('Usage'),
    userSettings: () => createStaticBreadcrumb.t('User Settings'),
    notificationPreferences: () => createStaticBreadcrumb.t('Notification Preferences'),
    sourceConnectors: () => createStaticBreadcrumb.t('Source & Connectors'),
    policies: (route: RouteLocationNormalized) => {
      const isAcknowledgmentsSection = route.matched.some(
        (r) => r.name === 'policies-acknowledgments',
      );
      const isMyPoliciesSection = route.matched.some((r) => r.name === 'my-policies');
      const isTemplatesCatalogSection = route.matched.some(
        (r) => r.name === 'policies-templates-catalog',
      );

      let to = '/policies/management';
      if (isMyPoliciesSection) to = '/policies/my-policies';
      else if (isAcknowledgmentsSection) to = '/policies/acknowledgments';
      else if (isTemplatesCatalogSection) to = '/policies/templates-catalog';

      return { label: i18n.global.t('Policy Center'), to };
    },
    policyVersions: () => createStaticBreadcrumb.t('Versions'),
    policyManagement: () => createStaticBreadcrumb.t('Policy Management'),
    myPolicies: () => createStaticBreadcrumb.t('My Tasks'),
    acknowledgments: () => createStaticBreadcrumb.t('Acknowledgements'),
    templatesCatalog: () => createStaticBreadcrumb.t('Templates Catalog'),
  },
} as const;
